'use client'

import { useContext, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { Cell, Dialog, Loader } from '@vinted/web-ui'
import { v4 as uuid } from 'uuid'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import {
  useConversationContext,
  ConversationListContext,
} from '@marketplace-web/messaging/conversation-providers-feature'
import { TransactionSide } from '@marketplace-web/messaging/conversation-messaging-data'
import {
  makeOfferEvent,
  OfferRequestPriceSuggestionModel,
  SellerOfferOptionsModel,
  SellerOfferPriceSuggestionModel,
  getOfferRequestOptions,
  OfferRequestOptionsModel,
  transformOfferRequestOptions,
  OfferPriceSuggestionOption,
  createOffer,
  createOfferRequest,
  CUSTOM_OFFER_PRICE_OPTION_INDEX,
} from '@marketplace-web/offers/price-offers-data'

import { OfferFormModel } from '../types'
import BuyerOfferRequestDialog from '../BuyerOfferRequestDialog'
import SellerOfferDialog from '../SellerOfferDialog'
import BuyerOfferRequestsExceededModal from '../BuyerOfferRequestsExceededModal'
import useGetPendingSellerOfferMessage from '../../hooks/useGetPendingSellerOfferMessage'
import { OfferFormField } from '../constants'

type Props = {
  conversationId: number | string
  isOpen: boolean
  itemId: number
  itemThumbnailUrl?: string | null
  itemTitle?: string
  onClose: () => void
  orderItemIds: Array<number>
  price: { amount: string; currencyCode: string }
  sellerId?: number
  sellerOfferOptions?: SellerOfferOptionsModel
  transactionId: number
  userSide: TransactionSide
}

const MakeOffer = ({
  conversationId,
  isOpen,
  itemId,
  itemThumbnailUrl,
  itemTitle,
  onClose,
  orderItemIds,
  price,
  sellerId,
  sellerOfferOptions,
  transactionId,
  userSide,
}: Props) => {
  const { track } = useTracking()
  const { locale } = useIntl()

  const formMethods = useForm<OfferFormModel>({ mode: 'onChange' })
  const {
    reset,
    setError,
    handleSubmit,
    formState: { isSubmitting },
  } = formMethods
  const pendingSellerOfferMessage = useGetPendingSellerOfferMessage()
  const { getConversationRequest } = useConversationContext()
  const { updateConversation } = useContext(ConversationListContext)

  const [offerRequestSessionId, setOfferRequestSessionId] = useState<string>()
  const [offerRequestOptions, setOfferRequestOptions] = useState<OfferRequestOptionsModel>()
  const [selectedOfferRequestPriceSuggestion, setSelectedOfferRequestPriceSuggestion] =
    useState<OfferPriceSuggestionOption>()

  const [loaderState, setLoaderState] = useState<UiState>()
  const [isLoaderVisible, setIsLoaderVisible] = useState(false)

  const isBuyer = userSide === TransactionSide.Buyer

  const isOfferRequestGuidanceV3AbTestEnabled = !!offerRequestOptions?.offerSuggestions

  const remainingOffers = offerRequestOptions?.remainingOfferCount
  const thresholdPrice = isBuyer
    ? offerRequestOptions?.feesReductionThreshold
    : sellerOfferOptions?.feesReductionThreshold
  const formattedThresholdPrice = thresholdPrice
    ? formatCurrencyAmount(thresholdPrice, locale)
    : null

  useEffect(() => {
    if (!isOpen || !isBuyer) return

    setOfferRequestSessionId(uuid())
  }, [isOpen, isBuyer, setOfferRequestSessionId])

  useEffect(() => {
    if (!isOpen) return

    reset({
      [OfferFormField.Offer]: '',
    })
  }, [reset, isOpen, isBuyer, offerRequestOptions])

  useEffect(() => {
    if (!isBuyer) return
    if (!price || !sellerId) return

    const fetchOfferRequestOptions = async () => {
      const response = await getOfferRequestOptions({ itemPrice: price, sellerId })

      if ('errors' in response) return

      const transformedOfferRequestOptions = transformOfferRequestOptions(response.request_options)

      setOfferRequestOptions(transformedOfferRequestOptions)

      const isOfferGuidanceEnabled = !!response.request_options.offer_suggestions

      if (isOfferGuidanceEnabled) {
        setSelectedOfferRequestPriceSuggestion({ isCustom: true })
      }
    }

    fetchOfferRequestOptions()
  }, [price, isBuyer, sellerId])

  useEffect(() => {
    if (!isOpen || !isOfferRequestGuidanceV3AbTestEnabled) return undefined

    return () => {
      setSelectedOfferRequestPriceSuggestion({ isCustom: true })
    }
  }, [isOpen, isOfferRequestGuidanceV3AbTestEnabled, setSelectedOfferRequestPriceSuggestion])

  const trackOfferSubmit = (
    formattedOfferValue: string,
    selectedSuggestion?: OfferPriceSuggestionOption | null,
    priceSuggestions?:
      | Array<OfferRequestPriceSuggestionModel>
      | Array<SellerOfferPriceSuggestionModel>
      | null,
    offerRequestId?: number,
    offerSessionId?: string,
  ) => {
    if (!price || !transactionId) return

    const selectedOfferPriceSuggestionIndex =
      priceSuggestions?.findIndex(
        suggestion => suggestion.price === selectedSuggestion?.suggestion?.price,
      ) ?? -1 // needed when priceSuggestions is null so offerSuggestionOptionNumber would resolve to null in following lines
    const optionIndex = selectedSuggestion?.isCustom
      ? CUSTOM_OFFER_PRICE_OPTION_INDEX
      : selectedOfferPriceSuggestionIndex

    track(
      makeOfferEvent({
        isBuyer,
        transactionId,
        offerRequestId,
        currentPrice: Number(price.amount),
        offeredPrice: Number(formattedOfferValue),
        offerSuggestionOptionNumber: optionIndex === -1 ? null : optionIndex + 1,
        offerRequestSessionId,
        offerSessionId,
        pendingSellerOfferId:
          isBuyer && pendingSellerOfferMessage ? pendingSellerOfferMessage.id : undefined,
      }),
    )
  }

  const renderLoader = () => (
    <Dialog contentDimensions={{ width: 'auto' }} testId="loader" show={isLoaderVisible}>
      <Cell>
        <Loader size="x-large" state={loaderState === UiState.Success ? 'success' : undefined} />
      </Cell>
    </Dialog>
  )

  const handleFormSubmit = async (formValues: OfferFormModel, offerSessionId?: string) => {
    const formattedOfferValue = formValues.offer.replace(',', '.')

    setIsLoaderVisible(true)

    if (!transactionId || !price || !conversationId) return

    const currency = price.currencyCode
    const response = isBuyer
      ? await createOfferRequest({ transactionId, price: formattedOfferValue, currency })
      : await createOffer({ transactionId, price: formattedOfferValue, currency })

    if ('errors' in response) {
      response.errors.forEach(() => {
        setError(OfferFormField.Offer, { type: 'manual', message: response.errors[0]!.value })
      })

      setIsLoaderVisible(false)

      return
    }

    const offerRequestId = isBuyer
      ? 'offer_request' in response && response.offer_request.id
      : 'offer' in response && response.offer.id

    const priceSuggestions = isBuyer ? offerRequestOptions?.offerSuggestions : null
    const selectedSuggestion = isBuyer ? selectedOfferRequestPriceSuggestion : null

    trackOfferSubmit(
      formattedOfferValue,
      selectedSuggestion,
      priceSuggestions,
      offerRequestId || undefined,
      offerSessionId,
    )
    onClose()
    setLoaderState(UiState.Success)
    getConversationRequest({ conversationId })
    updateConversation({ id: conversationId })
  }

  const handleBuyerFormSubmit = async (formValues: OfferFormModel) => {
    return handleFormSubmit(formValues)
  }

  const renderOfferDialog = () => {
    if (!price) return null

    if (isBuyer)
      return (
        <BuyerOfferRequestDialog
          show={isOpen}
          itemPrice={price}
          minItemPrice={offerRequestOptions?.minPrice}
          maxItemPrice={offerRequestOptions?.maxPrice}
          maxDiscount={offerRequestOptions?.maxDiscount}
          thresholdPrice={formattedThresholdPrice}
          isSubmitting={isSubmitting}
          transactionId={transactionId}
          remainingOffers={remainingOffers}
          offerPriceSuggestions={offerRequestOptions?.offerSuggestions}
          onSubmit={handleSubmit(handleBuyerFormSubmit)}
          onClose={onClose}
          itemId={itemId}
          itemTitle={itemTitle}
          itemThumbnailUrl={itemThumbnailUrl}
          orderItemIds={orderItemIds}
          offerRequestSessionId={offerRequestSessionId}
          selectedOfferRequestPriceSuggestion={selectedOfferRequestPriceSuggestion}
          onSelectOfferRequestPriceSuggestion={setSelectedOfferRequestPriceSuggestion}
        />
      )

    return (
      <SellerOfferDialog
        show={isOpen}
        itemPrice={price}
        minItemPrice={sellerOfferOptions?.minPrice}
        maxItemPrice={sellerOfferOptions?.maxPrice}
        thresholdPrice={formattedThresholdPrice}
        isSubmitting={isSubmitting}
        onSubmit={(formValues, offerSessionId) => handleFormSubmit(formValues, offerSessionId)}
        onClose={onClose}
        itemId={itemId}
        itemTitle={itemTitle}
        itemThumbnailUrl={itemThumbnailUrl}
        orderItemIds={orderItemIds}
        transactionId={transactionId}
      />
    )
  }

  return (
    <>
      {isBuyer && !remainingOffers ? (
        <BuyerOfferRequestsExceededModal
          show={isOpen}
          onClose={onClose}
          maxOfferCount={offerRequestOptions?.maxOfferCount}
        />
      ) : (
        <FormProvider {...formMethods}>{renderOfferDialog()}</FormProvider>
      )}
      {renderLoader()}
    </>
  )
}

export default MakeOffer
